import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Rocket, Zap, ShieldCheck, Play, Tv, Wifi, Search, Smartphone, List, Download, Settings, Layout } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Primeiros Passos no Mundo IPTV", href: "/blog/primeiros-passos-mundo-iptv" }
  ];

  const faq = [
    {
      question: "Qual o primeiro passo para ter IPTV?",
      answer: "O primeiro passo é escolher um servidor de confiança como a P2 PRO e realizar um teste grátis para verificar a compatibilidade com sua internet e aparelhos."
    },
    {
      question: "Preciso comprar algum aparelho especial?",
      answer: "Se você já tem uma Smart TV moderna ou um smartphone, não precisa comprar nada extra. Caso sua TV seja antiga, um Fire Stick ou Mi Box são os investimentos recomendados."
    },
    {
      question: "Onde consigo os dados de acesso?",
      answer: "Após solicitar o teste ou assinar um plano, nossa equipe envia via WhatsApp ou E-mail seu usuário, senha e a URL do servidor."
    }
  ];

  const benefits = [
    {
      title: "Configuração Rápida",
      description: "Saia do zero e tenha milhares de canais funcionando em menos de 5 minutos.",
      icon: <Rocket className="w-6 h-6" />
    },
    {
      title: "Independência Total",
      description: "Aprenda a configurar seus próprios dispositivos sem depender de visitas técnicas.",
      icon: <Settings className="w-6 h-6" />
    },
    {
      title: "Economia Imediata",
      description: "Cancele assinaturas caras e comece a economizar já no primeiro mês.",
      icon: <Zap className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Escolha o Plano",
      description: "Defina se você precisa de 1 ou mais telas simultâneas."
    },
    {
      title: "Instale o App",
      description: "Baixe o player oficial da P2 PRO na loja de apps do seu dispositivo."
    },
    {
      title: "Login Seguro",
      description: "Insira suas credenciais únicas fornecidas pela nossa equipe."
    },
    {
      title: "Navegação",
      description: "Explore as categorias e favorize seus canais preferidos."
    }
  ];

  const diferenciais = [
    {
      title: "Onboarding VIP",
      description: "Nós guiamos você por vídeo ou chat durante toda a sua primeira configuração.",
      icon: <Smartphone className="w-6 h-6" />
    },
    {
      title: "App Intuitivo",
      description: "Nossos aplicativos são desenhados para que até quem nunca usou tecnologia consiga operar.",
      icon: <Layout className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. O Despertar para o Novo Entretenimento",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Dar os <strong>primeiros passos no mundo IPTV</strong> é como descobrir um novo universo de possibilidades. Imagine que, até ontem, você estava limitado a meia dúzia de canais locais e pacotes engessados de operadoras. Hoje, você está prestes a ter o controle total da sua TV. O IPTV não é apenas uma forma de ver televisão; é uma forma de personalizar seu estilo de vida digital.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia inicial, vamos pegar você pela mão. Não importa se você não entende de tecnologia ou se nunca ouviu falar em 'streaming'. Na <strong>P2 PRO</strong>, simplificamos o complexo. Vamos mostrar que configurar seu primeiro acesso é tão fácil quanto mandar uma mensagem no WhatsApp.
          </p>
        </div>
      )
    },
    {
      title: "2. Preparando o Terreno: O Que Você Já Tem em Casa",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Tv className="w-6 h-6 text-electric" /> Sua Smart TV</h4>
              <p className="text-sm text-muted-foreground">Procure por aplicativos como 'IBO Player', 'Bob Player' ou o nosso app próprio na loja de aplicativos da sua TV.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Wifi className="w-6 h-6 text-amber-500" /> Seu Roteador</h4>
              <p className="text-sm text-muted-foreground">O local onde o roteador está instalado influencia no sinal. Mantenha-o próximo à TV ou use um cabo de rede.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. O Ritual da Primeira Configuração",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Siga este passo a passo para garantir que seu primeiro dia com IPTV seja perfeito:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Baixe o Player Correto</h4>
              <p className="text-muted-foreground">Cada dispositivo tem um 'melhor app'. Na Smart TV Samsung use um, no Celular Android use outro. Nossa equipe indicará o ideal para você.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Teste de Conexão Inicial</h4>
              <p className="text-muted-foreground">Abra um canal em HD primeiro. Se rodar liso, passe para o Full HD e depois para o 4K. Assim você conhece o limite da sua internet.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas de Segurança para o Novo Usuário",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Mantenha sua experiência protegida desde o início com estas práticas recomendadas:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Nunca compartilhe sua senha</h5>
                <p className="text-sm text-muted-foreground">Seu login é pessoal e intransferível. O uso simultâneo além do permitido no plano causará bloqueio automático.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Cuidado com sites de 'Geradores de Lista'</h5>
                <p className="text-sm text-muted-foreground">Muitos sites prometem listas grátis, mas roubam seus dados. Use apenas os links oficiais fornecidos pela P2 PRO.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Suporte via WhatsApp</h5>
                <p className="text-sm text-muted-foreground">Salve nosso número de suporte. No mundo IPTV, uma dúvida resolvida rápido é a garantia de um lazer sem interrupções.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Primeiros Passos no Mundo IPTV: O Manual do Novo Usuário"
        subtitle="Sua jornada para o entretenimento de elite começa aqui. Aprenda a configurar seu primeiro acesso em minutos."
        heroImage="https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Você decidiu mudar sua forma de ver TV, e essa é uma das melhores decisões de consumo que você tomou este ano. Mas, como toda novidade, o IPTV pode gerar algumas dúvidas iniciais. 'Onde eu baixo o app?', 'Como eu coloco a senha?', 'Por que o canal 4K não carrega?'. Fique tranquilo, todos os usuários de elite um dia foram iniciantes e passaram por esse processo.",
          "Neste guia de Primeiros Passos, vamos focar no que realmente importa para você começar a assistir hoje. Sem rodeios, vamos direto aos aplicativos recomendados, à forma correta de inserir seus dados e a como navegar pela imensidão de conteúdo da P2 PRO de forma organizada.",
          "O objetivo aqui é transformar a complexidade técnica em diversão pura. Em menos de 10 minutos de leitura, você estará pronto para dar o play na sua nova vida digital."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🎯 Pronto para Dar o Primeiro Passo?"
        ctaDescription="Não perca mais tempo com TV comum. Solicite agora seu acesso P2 PRO e comece a maratonar seus conteúdos favoritos."
        ctaButtonText="Solicitar Meu Teste Agora"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/primeiros-passos-mundo-iptv")({
  head: () => ({
    meta: [
      { title: "Primeiros Passos no Mundo IPTV: Guia Prático 2026" },
      { name: "description", content: "Aprenda a configurar seu IPTV pela primeira vez. Guia passo a passo com aplicativos, logins e dicas para começar a assistir em minutos." },
    ],
  }),
  component: ArticlePage,
});
